import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "./MiniChart.css";

// Registra os componentes e o plugin para exibir rótulos
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  ChartDataLabels
);

const MiniChart = ({ dataPoints = [], color = "#6366f1" }) => {
  const [chartKey, setChartKey] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  // Garante que sempre haverá 7 valores, preenchendo com zero
  const dadosCompletos =
    dataPoints.length === 7
      ? dataPoints
      : dataPoints.concat(new Array(7 - dataPoints.length).fill(0));

  // Define o índice do dia atual (ajustado para semana começar em segunda)
  const hoje = new Date();
  const diaHoje = hoje.getDay(); // 0 = domingo, ..., 6 = sábado
  const diaIndex = diaHoje === 0 ? 6 : diaHoje - 1;

  // Calcula estatísticas dos dados
  const maxValue = Math.max(...dadosCompletos, 1);
  const minValue = Math.min(...dadosCompletos);
  const avgValue = (dadosCompletos.reduce((a, b) => a + b, 0) / 7).toFixed(1);
  const totalValue = dadosCompletos.reduce((a, b) => a + b, 0);

  // Animação ao montar o componente
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setChartKey((prev) => prev + 1);
    }, 100);
    return () => clearTimeout(timer);
  }, [dataPoints]);

  // Função para determinar cor com base no valor
  const getColorIntensity = (value) => {
    if (value === 0) return `${color}20`;
    if (value <= maxValue * 0.33) return `${color}40`;
    if (value <= maxValue * 0.66) return `${color}60`;
    return `${color}99`;
  };

  // Configuração de cores para os pontos
  const pointColors = dadosCompletos.map((value, idx) => {
    if (idx === diaIndex) return "#ffc107"; // Dia atual em amarelo
    if (value === 0) return "#cbd5e1"; // Sem dados em cinza
    if (value >= maxValue * 0.7) return color; // Valores altos na cor principal
    return `${color}99`; // Valores menores com transparência
  });

  const data = {
    labels,
    datasets: [
      {
        data: dadosCompletos,
        borderColor: color,
        backgroundColor: `${color}15`,
        fill: true,
        tension: 0.4,
        borderWidth: 2.5,
        pointRadius: dadosCompletos.map((_, idx) => (idx === diaIndex ? 7 : 5)),
        pointHoverRadius: 8,
        pointBackgroundColor: pointColors,
        pointBorderColor: dadosCompletos.map((_, idx) =>
          idx === diaIndex ? "#fff" : color
        ),
        pointBorderWidth: 2,
        segment: {
          borderColor: (ctx) => {
            const value = ctx.p1DataIndex !== undefined ? dadosCompletos[ctx.p1DataIndex] : 0;
            return value > 0 ? color : `${color}40`;
          },
        },

        // Rótulos sobre os pontos com valores visíveis
        datalabels: {
          display: true,
          color: color,
          font: {
            weight: "bold",
            size: 12,
          },
          anchor: "end",
          align: "top",
          offset: 12,
          clamp: true,
          clip: false,
          formatter: (value) => (value > 0 ? value : ""),
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
    elements: {
      line: { borderWidth: 2.5 },
    },
    plugins: {
      legend: { display: false },
      datalabels: { display: true },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(30, 41, 59, 0.95)",
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 12,
        displayColors: false,
        borderColor: color,
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          title: (context) => {
            const dayIndex = context[0].dataIndex;
            return `${labels[dayIndex]}`;
          },
          label: (tooltipItem) => {
            const value = tooltipItem.raw;
            return value > 0 ? `Chamadas: ${value}` : "Sem dados";
          },
          afterLabel: (tooltipItem) => {
            const value = tooltipItem.raw;
            if (value > 0) {
              const percentage = ((value / totalValue) * 100).toFixed(1);
              return `${percentage}% do total`;
            }
            return "";
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: { size: 11, weight: "600" },
          color: "#64748b",
          padding: 8,
        },
        grid: {
          color: "#e2e8f0",
          drawBorder: false,
          lineWidth: 0.5,
        },
        border: {
          display: false,
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        max: Math.ceil(maxValue * 1.2),
      },
    },
  };

  return (
    <div className={`mini-chart-container ${isAnimating ? "animate" : ""}`}>
      <div className="chart-wrapper">
        <Line key={chartKey} data={data} options={options} />
      </div>

      {/* Estatísticas do Gráfico */}
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Total</span>
          <span className="stat-value">{totalValue}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Média</span>
          <span className="stat-value">{avgValue}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Máximo</span>
          <span className="stat-value">{maxValue}</span>
        </div>
      </div>
    </div>
  );
};

export default MiniChart;
